import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Mail } from "lucide-react";

export default function CancellationPolicyPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="cancellation-policy-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-muted">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">
              Our Policy
            </p>
            <h1 className="heading-xl mb-6">
              Cancellation <em className="italic">Policy</em>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Policy Content */}
      <section className="section-spacing">
        <div className="container-custom max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-10"
          >
            <p className="body-text text-muted-foreground leading-relaxed">
              All bookings require a £20 deposit at the time of booking to secure your appointment. 
              The remaining balance is payable on arrival by cash or bank transfer. 
              Please review our cancellation and rescheduling policy below.
            </p>

            {/* Policy Tiers */}
            <div className="space-y-6">
              <div className="border border-border p-6 lg:p-8">
                <div className="flex items-start gap-4">
                  <span className="bg-primary/10 text-primary text-xs font-medium px-3 py-1 tracking-wide uppercase flex-shrink-0">
                    48+ hrs
                  </span>
                  <div>
                    <h3 className="font-serif text-lg text-foreground mb-2">Cancellation with 48+ hours' notice</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Your £20 deposit can be refunded or transferred to a new appointment, depending on the arrangement.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border border-border p-6 lg:p-8">
                <div className="flex items-start gap-4">
                  <span className="bg-red-50 text-red-700 text-xs font-medium px-3 py-1 tracking-wide uppercase flex-shrink-0">
                    &lt;48 hrs
                  </span>
                  <div>
                    <h3 className="font-serif text-lg text-foreground mb-2">Cancellation with less than 48 hours' notice</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      The £20 deposit is <strong>non-refundable</strong>. This time has been reserved exclusively for you, 
                      and late cancellations mean we are unable to offer the slot to someone else.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border border-border p-6 lg:p-8">
                <div className="flex items-start gap-4">
                  <span className="bg-red-50 text-red-700 text-xs font-medium px-3 py-1 tracking-wide uppercase flex-shrink-0">
                    No-Show
                  </span>
                  <div>
                    <h3 className="font-serif text-lg text-foreground mb-2">No-show</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      If you do not attend your appointment without notice, the £20 deposit is <strong>non-refundable</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Rescheduling */}
            <div>
              <h2 className="font-serif text-xl text-foreground mb-3">Rescheduling</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                You may reschedule your appointment with a minimum of <strong>48 hours' notice</strong>. 
                If at least 48 hours' notice is given, your £20 deposit will be transferred to your new appointment date. 
                Rescheduling with less than 48 hours' notice is not guaranteed and may result in the loss of your deposit.
              </p>
            </div>

            {/* Late Arrivals */}
            <div>
              <h2 className="font-serif text-xl text-foreground mb-3">Late Arrivals</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                If you arrive late, your session may need to be shortened to avoid disrupting 
                the next appointment. The full session fee will still apply. We recommend 
                arriving 5–10 minutes early so you can settle in and relax.
              </p>
            </div>

            {/* How to Cancel */}
            <div>
              <h2 className="font-serif text-xl text-foreground mb-3">How to Cancel or Reschedule</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                To cancel or reschedule your appointment, please get in touch as soon as possible:
              </p>
              <a
                href="mailto:hello@soundhealingmassage.com"
                className="inline-flex items-center gap-2 text-primary text-sm hover:underline"
              >
                <Mail className="w-4 h-4" />
                hello@soundhealingmassage.com
              </a>
            </div>

            {/* CTA */}
            <div className="pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground mb-6">
                We truly appreciate your understanding. This policy allows us to provide the 
                best possible experience for all of our clients.
              </p>
              <Link to="/booking">
                <Button className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground px-12 py-7 text-xs tracking-[0.2em] uppercase btn-animate">
                  Book a Session
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
